import type { AppPage } from "../context/MonyawnContext.types";

export type RouteSeoConfig = {
  page: AppPage;
  title: string;
  description: string;
  canonicalPath: string;
  robots: string;
  intro: string;
  breadcrumbLabel: string;
  faq?: Array<{ question: string; answer: string }>;
};

const INDEX_ROBOTS = "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1";
const NOINDEX_ROBOTS = "noindex,nofollow,noarchive";

export const routeSeoConfig: Record<AppPage, RouteSeoConfig> = {
  login: {
    page: "login",
    title: "Monyawn | Local-First Opportunity Management for High-Stakes Career Moves",
    description:
      "Monyawn helps professionals structure high-stakes job opportunities with evidence-backed workflows, private-by-default storage, and AI-assisted clarity.",
    canonicalPath: "/",
    robots: INDEX_ROBOTS,
    intro:
      "Monyawn is a local-first opportunity management platform for professionals navigating high-stakes career decisions.",
    breadcrumbLabel: "Home",
    faq: [
      {
        question: "What is Monyawn?",
        answer:
          "Monyawn is a local-first platform that helps people manage high-stakes career opportunities using evidence-backed workflows instead of scattered notes.",
      },
      {
        question: "Who is Monyawn built for?",
        answer:
          "Monyawn is built for professionals making important career moves who need a private workspace to organize signals, proof, messages, and decision checkpoints.",
      },
      {
        question: "Does Monyawn store my data in the cloud by default?",
        answer:
          "No. Monyawn is private by default and keeps your workspace in the browser unless you explicitly export or share.",
      },
    ],
  },
  start: {
    page: "start",
    title: "Start an Opportunity Play | Monyawn",
    description:
      "Start a new opportunity play from a message, link, transcript, or note. Monyawn structures your first signal into a guided action flow.",
    canonicalPath: "/start",
    robots: INDEX_ROBOTS,
    intro:
      "Start your opportunity workflow by dropping one real signal and moving from noise to a clean next step.",
    breadcrumbLabel: "Start",
  },
  "proof-drop": {
    page: "proof-drop",
    title: "Signal Intake | Monyawn",
    description:
      "Capture the first signal and structure opportunity data with guided conversational intake.",
    canonicalPath: "/signal-intake",
    robots: NOINDEX_ROBOTS,
    intro: "Signal intake is a private utility step inside your opportunity workspace.",
    breadcrumbLabel: "Signal Intake",
  },
  confirm: {
    page: "confirm",
    title: "Confirm Opportunity Intake | Monyawn",
    description: "Review and confirm structured opportunity intake details.",
    canonicalPath: "/confirm",
    robots: NOINDEX_ROBOTS,
    intro: "Confirmation is a private utility step for validating structured intake data.",
    breadcrumbLabel: "Confirm",
  },
  "setup-base": {
    page: "setup-base",
    title: "Set Up Opportunity Base | Monyawn",
    description: "Configure account and profile context before entering the workspace.",
    canonicalPath: "/setup-base",
    robots: NOINDEX_ROBOTS,
    intro: "Setup base is a private utility step for initializing workspace context.",
    breadcrumbLabel: "Setup Base",
  },
  workspace: {
    page: "workspace",
    title: "Opportunity Workspace | Monyawn",
    description:
      "Run your opportunity workflow with proof management, correspondence drafting, and stage progression in one controlled workspace.",
    canonicalPath: "/workspace",
    robots: NOINDEX_ROBOTS,
    intro: "Workspace is the operational cockpit for active opportunity execution.",
    breadcrumbLabel: "Workspace",
  },
  about: {
    page: "about",
    title: "About Monyawn | Product Truth, Privacy Model, and Operating Principles",
    description:
      "Learn what Monyawn is, who it serves, how local-first privacy works, and the principles behind evidence-backed opportunity management.",
    canonicalPath: "/about",
    robots: INDEX_ROBOTS,
    intro:
      "Monyawn helps people make high-stakes career decisions with structured evidence, clear narrative control, and local-first privacy.",
    breadcrumbLabel: "About",
    faq: [
      {
        question: "How does Monyawn support high-stakes career decisions?",
        answer:
          "Monyawn converts raw opportunity signals into structured workflows, so users can track proof, correspondence, and next-step decisions with less friction.",
      },
      {
        question: "What makes Monyawn different from generic job trackers?",
        answer:
          "Monyawn focuses on high-leverage opportunity execution: evidence-backed story building, governed handoff packages, and private-by-default workflow control.",
      },
    ],
  },
  support: {
    page: "support",
    title: "Support | Monyawn",
    description:
      "Get support for Monyawn workflows, troubleshooting, and product navigation. Find implementation docs and issue reporting paths.",
    canonicalPath: "/support",
    robots: INDEX_ROBOTS,
    intro: "Support resources for setup, troubleshooting, and product guidance.",
    breadcrumbLabel: "Support",
  },
  privacy: {
    page: "privacy",
    title: "Privacy | Monyawn",
    description:
      "Review Monyawn privacy policy, local-first storage behavior, optional processors, and user controls.",
    canonicalPath: "/privacy",
    robots: INDEX_ROBOTS,
    intro: "Monyawn is local-first and private by default.",
    breadcrumbLabel: "Privacy",
  },
  terms: {
    page: "terms",
    title: "Terms | Monyawn",
    description:
      "Read Monyawn terms covering use, responsibility boundaries, and product scope for local-first opportunity workflows.",
    canonicalPath: "/terms",
    robots: INDEX_ROBOTS,
    intro: "Terms for using the Monyawn platform responsibly.",
    breadcrumbLabel: "Terms",
  },
  cookies: {
    page: "cookies",
    title: "Cookies and Storage | Monyawn",
    description:
      "Understand the cookies, local storage, IndexedDB, and diagnostics storage used by Monyawn.",
    canonicalPath: "/cookies",
    robots: INDEX_ROBOTS,
    intro: "Storage technologies used by Monyawn and how to control them.",
    breadcrumbLabel: "Cookies and Storage",
  },
  security: {
    page: "security",
    title: "Security | Monyawn",
    description:
      "Review Monyawn's current security posture, local-first architecture, and responsible disclosure path.",
    canonicalPath: "/security",
    robots: INDEX_ROBOTS,
    intro: "Current product security posture and disclosure guidance.",
    breadcrumbLabel: "Security",
  },
  "acceptable-use": {
    page: "acceptable-use",
    title: "Acceptable Use | Monyawn",
    description:
      "Review the acceptable use restrictions and user responsibilities for the Monyawn platform.",
    canonicalPath: "/acceptable-use",
    robots: INDEX_ROBOTS,
    intro: "Rules that protect users, the platform, and CAD Guardian LLC's platform IP.",
    breadcrumbLabel: "Acceptable Use",
  },
};

export function getSeoForPage(page: AppPage): RouteSeoConfig {
  return routeSeoConfig[page];
}
